import {
  createElement,
  CSSProperties,
  forwardRef,
  isValidElement,
  MutableRefObject,
  PropsWithChildren,
  ReactElement,
  ReactNode,
  useRef,
} from 'react';
import BScroll from '@better-scroll/core';
import { hooks } from '../hooks/index.js';
import { ScrollarOptions } from './types.option.js';

const scrollWrapperStyle = {
  position: 'relative',
  overflow: 'hidden',
  height: '100%',
  width: '100%',
};

const scrollWrapperStyleScrollX = {
  display: 'flex',
  alignItems: 'flex-start',
};

const contentStyleScrollX = {
  flex: '1 1 auto',
};

/**
 * @see See [BetterScroll 2.0 Options](https://better-scroll.github.io/docs/en-US/guide/base-scroll-options.html)
 */
const bScrollOptionKeys: Array<keyof ScrollarOptions> = [
  'startX',
  'startY',
  'scrollX',
  'scrollY',
  'freeScroll',
  'directionLockThreshold',
  'eventPassthrough',
  'click',
  'dblclick',
  'tap',
  'bounce',
  'bounceTime',
  'momentum',
  'momentumLimitTime',
  'momentumLimitDistance',
  'swipeTime',
  'swipeBounceTime',
  'deceleration',
  'flickLimitTime',
  'flickLimitDistance',
  'resizePolling',
  'probeType',
  'stopPropagation',
  'preventDefault',
  'preventDefaultException',
  'tagException',
  'HWCompositing',
  'useTransition',
  'bindToWrapper',
  'bindToTarget',
  'disableMouse',
  'disableTouch',
  'autoBlur',
  'autoEndDistance',
  'outOfBoundaryDampingFactor',
  'specifiedIndexAsContent',
];

export interface ScrollbarProps extends ScrollarOptions {
  /**
   * wrapper容器className
   */
  className?: string;
  /**
   * wrapper容器style
   */
  style?: CSSProperties;
  /**
   * 是否给滚动内容，增加个`div`节点，处理`children`是数组的场景
   * @default true
   */
  addContentNode?: boolean;
  /**
   * BetterScroll插件
   * 传入格式：{plugins: [PullDown]};
   * 插件配置数据传入：{ plugins:[[PullDown, {pullDownRefresh: true}]] }
   */
  plugins?: Array<unknown | [unknown, Record<string, unknown>]>;
}

export type ScrollbarApi =
  import('@better-scroll/core/dist/types/BScroll.js').BScrollConstructor;

function renderChild(
  children: ReactNode | ((renderProps) => ReactElement),
  renderProps
): ReactElement {
  // React Element or null
  if (
    children === null ||
    Array.isArray(children) ||
    isValidElement(children)
  ) {
    return children as ReactElement;
  }
  // React render function
  if (typeof children === 'function') {
    return children(renderProps);
  }
  // Create React components.
  return createElement(children as string, renderProps);
}

export const Scrollbar: React.ForwardRefExoticComponent<
  PropsWithChildren<ScrollbarProps> & React.RefAttributes<ScrollbarApi>
> = forwardRef((props, ref) => {
  const {
    className,
    addContentNode = true,
    style,
    plugins = [],
    children,
  } = props;
  const bScrollRef = useRef<ScrollbarApi>();
  const bScrollWrapperRef = useRef<HTMLDivElement>(null);

  hooks.useCustomCompareEffect(
    () => {
      (plugins || []).forEach((plugin) => {
        (BScroll['default'] || BScroll).use(
          Array.isArray(plugin) ? plugin[0] : plugin
        );
      });
    },
    [plugins],
    (newData, oldData) => {
      return (
        Array.isArray(newData) &&
        Array.isArray(oldData) &&
        newData.length === oldData.length
      );
    }
  );

  // 解析core配置，不能直接赋值undefined，这样BScroll会视为配置值就是undefined,而不走默认值逻辑
  const bScrollOptions = bScrollOptionKeys.reduce<Record<string, unknown>>(
    (bScrollOption, key) => {
      if (props[key] != null) {
        bScrollOption[key] = props[key];
      }
      return bScrollOption;
    },
    {}
  );

  // 先解析plugin配置
  const bScrollOption = (plugins || []).reduce<Record<string, unknown>>(
    (pluginOpts, plugin) => {
      if (Array.isArray(plugin) && plugin[1]) {
        pluginOpts = Object.assign(pluginOpts, plugin[1]);
      }
      return pluginOpts;
    },
    bScrollOptions
  );

  /**
   * For Performance purpuse  we don't add `plugins` to
   * useEffect dependencies watch.
   */
  hooks.useDeepCompareEffect(() => {
    bScrollRef.current = new (BScroll['default'] || BScroll)(
      bScrollWrapperRef.current as HTMLElement,
      bScrollOption
    );
    if (ref) {
      (ref as MutableRefObject<ScrollbarApi>).current = bScrollRef.current;
    }
    // clean up
    return () => {
      bScrollRef.current?.destroy();
    };
  }, [bScrollOptions, ref]);

  const isScrollX = props.scrollX === true && props.scrollY === false; // use BScroll plugins
  const newStyle = Object.assign(
    {},
    scrollWrapperStyle,
    isScrollX ? scrollWrapperStyleScrollX : null,
    style
  );

  return (
    <div className={className} style={newStyle} ref={bScrollWrapperRef}>
      {!addContentNode ? (
        renderChild(children, {
          scroll: bScrollRef,
        })
      ) : (
        <div style={isScrollX ? contentStyleScrollX : {}}>
          {renderChild(children, {
            scroll: bScrollRef,
          })}
        </div>
      )}
    </div>
  );
});
